/**
 * The image itself, rendered the way `apps/assets/CLAUDE.md` asks for:
 * responsive `srcset`, `placeholder_color` behind it so there is no white flash
 * before it decodes, and a real `alt` attribute.
 *
 * Alt text is deliberately NOT faked from the filename. The library shows the
 * filename as visible text beside every tile, so an image with no alt text is
 * decorative here (`alt=""`) and gets a visible "Needs alt text" flag instead —
 * inventing `alt="brand-logo-final-v2.png"` would make an accessibility audit
 * look clean while telling a screen-reader user nothing.
 */

import { cn } from '@/utils';
import { thumbSrc, type MediaAsset } from '@/services/assets';

/** Transparency checkerboard, so an alpha PNG does not read as a white block. */
export const CHECKERBOARD =
  'repeating-conic-gradient(rgba(148,163,184,0.35) 0% 25%, transparent 0% 50%) 50% / 16px 16px';

interface AssetThumbProps {
  asset: MediaAsset;
  className?: string;
  /** Responsive `sizes` hint. Defaults to a grid tile. */
  sizes?: string;
  /** Use the full-size file instead of the thumbnail rendition. */
  full?: boolean;
  objectFit?: 'contain' | 'cover';
}

export function AssetThumb({
  asset,
  className,
  sizes = '(min-width: 1280px) 220px, (min-width: 768px) 25vw, 45vw',
  full = false,
  objectFit = 'contain',
}: AssetThumbProps) {
  const src = full ? asset.url || asset.stable_url : thumbSrc(asset);
  const isVector = asset.render_hint === 'img-only';

  return (
    <div
      className={cn('relative flex h-full w-full items-center justify-center overflow-hidden', className)}
      style={{
        background: asset.has_alpha ? CHECKERBOARD : asset.placeholder_color || undefined,
      }}
    >
      <img
        src={src}
        // A vector has no width-based renditions, so a srcset would just repeat
        // the same file at every descriptor.
        srcSet={!isVector && asset.srcset_webp ? asset.srcset_webp : undefined}
        sizes={!isVector && asset.srcset_webp ? sizes : undefined}
        alt={asset.alt_text || ''}
        loading="lazy"
        decoding="async"
        className={cn(
          'h-full w-full',
          objectFit === 'cover' ? 'object-cover' : 'object-contain',
        )}
      />
    </div>
  );
}
